import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Star, MapPin, BookOpen, Map as MapIcon, Info, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import MenuSection from "@/pages/admin/sections/MenuSection";
import FloorSection from "@/pages/admin/sections/FloorSection";

const TABS = ["info", "menu", "floor"];

export default function AdminRestaurantDetailPage() {
    const { restaurantId, section } = useParams();
    const navigate = useNavigate();
    const tab = TABS.includes(section) ? section : "info";

    const [restaurant, setRestaurant] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get(`/restaurants/${restaurantId}`);
            setRestaurant(data);
        } catch (error) {
            console.warn("[admin-restaurant-detail] load failed", error?.message);
        } finally {
            setLoading(false);
        }
    }, [restaurantId]);

    useEffect(() => {
        load();
    }, [load]);

    const changeTab = (next) => {
        navigate(`/admin/restaurants/${restaurantId}/${next}`, { replace: true });
    };

    if (loading) {
        return <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8"><Skeleton className="h-80 w-full" /></div>;
    }
    if (!restaurant) {
        return (
            <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12 text-center">
                <h1 className="font-display text-2xl">Restaurant not found</h1>
                <Button className="mt-4" onClick={() => navigate("/admin/restaurants")}>Back to restaurants</Button>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6">
            <Link to="/admin/restaurants" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-3" data-testid="admin-back-to-restaurants">
                <ArrowLeft className="h-4 w-4 mr-1" /> All restaurants
            </Link>

            <Card className="overflow-hidden mb-6">
                <div className="grid md:grid-cols-[280px_1fr]">
                    <div className="aspect-video md:aspect-auto md:h-full">
                        <img src={restaurant.image_url} alt={restaurant.name} className="h-full w-full object-cover" />
                    </div>
                    <CardContent className="p-6">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                                <h1 className="font-display text-3xl">{restaurant.name}</h1>
                                <div className="text-sm text-muted-foreground inline-flex items-center gap-1.5 mt-1">
                                    <MapPin className="h-3.5 w-3.5" /> {restaurant.address}
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <Badge variant="outline"><Star className="h-3 w-3 mr-1 fill-current" /> {Number(restaurant.rating).toFixed(1)}</Badge>
                                <Badge variant="secondary">{restaurant.cuisine}</Badge>
                                <Badge variant="outline">{restaurant.price_range}</Badge>
                            </div>
                        </div>
                        <p className="text-sm text-muted-foreground mt-3">{restaurant.description}</p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-xs">
                            <Stat label="Open" value={`${restaurant.opens_at} – ${restaurant.closes_at}`} />
                            <Stat label="Tax rate" value={`${(restaurant.tax_rate * 100).toFixed(1)}%`} />
                            <Stat label="Reviews" value={restaurant.review_count?.toLocaleString?.() ?? "0"} />
                            <Stat label="ID" value={restaurant.id.slice(0, 8) + "…"} />
                        </div>
                    </CardContent>
                </div>
            </Card>

            <Tabs value={tab} onValueChange={changeTab} data-testid="admin-restaurant-tabs">
                <TabsList>
                    <TabsTrigger value="info" data-testid="admin-tab-info"><Info className="h-3.5 w-3.5 mr-1" /> Info</TabsTrigger>
                    <TabsTrigger value="menu" data-testid="admin-tab-menu"><BookOpen className="h-3.5 w-3.5 mr-1" /> Menu</TabsTrigger>
                    <TabsTrigger value="floor" data-testid="admin-tab-floor"><MapIcon className="h-3.5 w-3.5 mr-1" /> Floor plan</TabsTrigger>
                </TabsList>
                <TabsContent value="info" className="mt-4">
                    <InfoSection restaurant={restaurant} onSaved={load} onDeleted={() => navigate("/admin/restaurants")} />
                </TabsContent>
                <TabsContent value="menu" className="mt-4">
                    <MenuSection restaurantId={restaurantId} />
                </TabsContent>
                <TabsContent value="floor" className="mt-4">
                    <FloorSection restaurantId={restaurantId} />
                </TabsContent>
            </Tabs>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div>
            <div className="text-muted-foreground">{label}</div>
            <div className="font-medium">{value}</div>
        </div>
    );
}

function InfoSection({ restaurant, onSaved, onDeleted }) {
    const [form, setForm] = useState({ ...restaurant });
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/admin/restaurants/${restaurant.id}`, form);
            toast.success("Updated");
            onSaved?.();
        } catch (error) {
            console.warn("[admin-restaurant] save failed", error?.message);
            toast.error(error?.response?.data?.detail || "Could not save");
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!window.confirm(`Delete ${restaurant.name}? This removes its menu and floor plan too.`)) return;
        setDeleting(true);
        try {
            await api.delete(`/admin/restaurants/${restaurant.id}`);
            toast.success("Deleted");
            onDeleted?.();
        } catch (error) {
            console.warn("[admin-restaurant] delete failed", error?.message);
            toast.error("Could not delete");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Card>
            <CardContent className="p-6 space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                    <FormRow label="Name"><Input value={form.name || ""} onChange={(e) => update("name", e.target.value)} data-testid="info-form-name" /></FormRow>
                    <FormRow label="Cuisine"><Input value={form.cuisine || ""} onChange={(e) => update("cuisine", e.target.value)} /></FormRow>
                </div>
                <FormRow label="Description"><Textarea value={form.description || ""} onChange={(e) => update("description", e.target.value)} /></FormRow>
                <FormRow label="Image URL"><Input value={form.image_url || ""} onChange={(e) => update("image_url", e.target.value)} /></FormRow>
                <FormRow label="Address"><Input value={form.address || ""} onChange={(e) => update("address", e.target.value)} /></FormRow>
                <div className="grid grid-cols-2 gap-3">
                    <FormRow label="Lat"><Input type="number" step="0.0001" value={form.lat ?? 0} onChange={(e) => update("lat", parseFloat(e.target.value))} /></FormRow>
                    <FormRow label="Lng"><Input type="number" step="0.0001" value={form.lng ?? 0} onChange={(e) => update("lng", parseFloat(e.target.value))} /></FormRow>
                </div>
                <div className="grid grid-cols-3 gap-3">
                    <FormRow label="Price">
                        <Select value={form.price_range || "$$"} onValueChange={(v) => update("price_range", v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{["$", "$$", "$$$", "$$$$"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                    </FormRow>
                    <FormRow label="Opens"><Input value={form.opens_at || ""} onChange={(e) => update("opens_at", e.target.value)} /></FormRow>
                    <FormRow label="Closes"><Input value={form.closes_at || ""} onChange={(e) => update("closes_at", e.target.value)} /></FormRow>
                </div>
                <FormRow label="Tax rate (decimal)"><Input type="number" step="0.001" value={form.tax_rate ?? 0} onChange={(e) => update("tax_rate", parseFloat(e.target.value))} /></FormRow>

                <div className="pt-3 border-t border-border">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Kitchen output</Label>
                    <p className="text-xs text-muted-foreground mt-1 mb-2">
                        Choose how this restaurant's kitchen receives new orders.
                    </p>
                    <Select value={form.kitchen_output || "screen"} onValueChange={(v) => update("kitchen_output", v)}>
                        <SelectTrigger data-testid="info-kitchen-output-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="screen">Screen — live Kanban display</SelectItem>
                            <SelectItem value="printer">Printer — printable paper tickets</SelectItem>
                            <SelectItem value="both">Both — screen and printer</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-border">
                    <Button variant="ghost" className="text-[hsl(var(--destructive))]" onClick={remove} disabled={deleting} data-testid="info-delete-button">
                        {deleting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
                        Delete restaurant
                    </Button>
                    <Button onClick={save} disabled={saving} data-testid="info-save-button">
                        {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Save changes
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}

function FormRow({ label, children }) {
    return (
        <div className="grid gap-1">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
            {children}
        </div>
    );
}
